import type { ReactNode } from "react";
import { t } from "../i18n";

// A list of rows on /shares, kept apart under headings — live from earlier
// shares, the recordings others asked me to review from mine — with its
// toolbar over it.

/**
 * A group of rows: its heading, and what acts on its rows alone (Select, for
 * the group whose rows may be picked), on the heading's line. `key` tells
 * apart groups whose titles may be the same (two students of one name); the
 * title does otherwise.
 */
export type Group<T> = {
  title: string;
  items: T[];
  className?: string;
  actions?: ReactNode;
  key?: string;
  /** given, the group is shown with none, this under its heading */
  none?: ReactNode;
  /** a line under the heading, over the rows: what the group is (byj-1wv.51.13) */
  note?: ReactNode;
  /** a heading without its count, for rows that are not a number of anything (settings) */
  uncounted?: boolean;
  /** the group's list's `data-testid`, for a test to find its rows (byj-1wv.51.17) */
  testid?: string;
};

/** A row's mark in its kind box: the first letter of what names it. */
export const initialOf = (name: string) => name.trim().charAt(0).toUpperCase() || "·";

/**
 * The toolbar (`tools`: the chips), then each group that has rows under its
 * heading, with its count and its actions, in the order given. When only one
 * group has rows there is no heading: that group's actions stand at the
 * toolbar's end, over the rows they reach; and with nothing to hold, no
 * toolbar is drawn at all. `headed` keeps the headings for a lone group too,
 * where a heading over other rows on the page would leave it unnamed.
 */
export function Grouped<T>({ id, tools, groups, row, headed: alwaysHeaded = false }: {
  id: string;
  tools?: ReactNode;
  groups: Group<T>[];
  row: (x: T) => ReactNode;
  headed?: boolean;
}) {
  const some = groups.filter((g) => g.items.length > 0 || g.none !== undefined);
  const headed = some.length > 1 || (alwaysHeaded && some.length > 0);
  const lone = headed ? null : some[0]?.actions;
  return (
    <>
      {(tools || lone) && (
        <div className="panel-tools row wrap" data-testid={`tools-${id}`}>
          {tools}
          {/* a lone group's actions at the line's end; nothing to push there, no spacer to share it */}
          {lone && <span className="grow" />}
          {lone}
        </div>
      )}
      {headed ? (
        <div data-testid={`rows-${id}`}>
          {some.map((g) => (
            <div key={g.key ?? g.title}>
              <div className="rows-head-line" role="group" aria-label={g.title}>
                <h3 className={`rows-head${g.className ? ` ${g.className}` : ""}`}>
                  {g.uncounted ? g.title : `${g.title} · ${g.items.length}`}
                </h3>
                {g.actions}
              </div>
              {g.note}
              {g.items.length > 0 ? (
                <ul className="rows" data-testid={g.testid}>
                  {g.items.map(row)}
                </ul>
              ) : (
                g.none
              )}
            </div>
          ))}
        </div>
      ) : some[0] && some[0].items.length === 0 ? (
        // a lone group with nothing in it: what it says in place of rows
        <div data-testid={`rows-${id}`}>{some[0].none}</div>
      ) : (
        <ul className="rows" data-testid={`rows-${id}`}>
          {(some[0]?.items ?? []).map(row)}
        </ul>
      )}
    </>
  );
}

/** What an empty list says, in place of its rows: a dashed card, the words muted. */
export function EmptyRows({ text }: { text: string }) {
  return (
    <div className="empty-card">
      <p className="muted small">{text}</p>
    </div>
  );
}

/** A group per value `of` gives — a student, a class — in the order each first appears among `items`. */
export function groupsBy<T>(items: readonly T[], of: (x: T) => { key: string; title: string }): Group<T>[] {
  const out = new Map<string, Group<T>>();
  for (const x of items) {
    const { key, title } = of(x);
    const g = out.get(key);
    if (g) g.items.push(x);
    else out.set(key, { key, title, items: [x] });
  }
  return [...out.values()];
}

/** The groups of a list of shares: live rows, then the rest, which carry `earlier`'s actions. */
export function liveThenEarlier<T extends { live?: boolean }>(items: T[], earlier?: ReactNode): Group<T>[] {
  return [
    { title: t("shares:liveNow"), items: items.filter((x) => x.live), className: "rows-head-live" },
    { title: t("shares:earlier"), items: items.filter((x) => !x.live), actions: earlier },
  ];
}
